/* Product subscription buy box (sections/main-product-subscription.liquid). */

// Section Rendering API via `sections=` (JSON). `section_id=` 404s through the theme dev proxy.
function wfFetchSection(url, sectionId) {
  const target = new URL(url);
  target.searchParams.set('sections', sectionId);
  return fetch(target.toString())
    .then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.json();
    })
    .then((sections) => {
      if (!sections[sectionId]) throw new Error('Section not rendered');
      return sections[sectionId];
    });
}

// Same-origin path of `url` with ?variant= set to `variantId` (other params kept); unchanged without an id.
function wfUrlWithVariant(url, variantId) {
  const target = new URL(url, window.location.origin);
  if (variantId) target.searchParams.set('variant', variantId);
  return target.pathname + target.search + target.hash;
}

if (!customElements.get('wf-drawer')) {
  class WfDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-wf-close]')) this.close();
      });
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    open(opener) {
      this.opener = opener;
      this.setAttribute('open', '');
      document.documentElement.classList.add('wf-lock');
      const panel = this.querySelector('.wf-drawer__panel');
      if (panel) requestAnimationFrame(() => panel.focus());
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      document.documentElement.classList.remove('wf-lock');
      if (this.opener) this.opener.focus();
    }
  }
  customElements.define('wf-drawer', WfDrawer);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-wf-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.wfOpen);
    if (drawer && drawer.open) {
      event.preventDefault();
      drawer.open(opener);
    }
  });
}

// Pill tabs (plant styles drawer): click or arrow keys switch panels.
if (!customElements.get('wf-tabs')) {
  class WfTabs extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-wf-tab]');
        if (tab) this.select(tab.dataset.wfTab, true);
      });
      this.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || !event.target.matches('[data-wf-tab]')) return;
        const tabs = [...this.querySelectorAll('[data-wf-tab]')];
        const i = tabs.indexOf(event.target);
        const next = { ArrowLeft: i - 1, ArrowRight: i + 1, Home: 0, End: tabs.length - 1 }[event.key];
        event.preventDefault();
        const target = tabs[(next + tabs.length) % tabs.length];
        this.select(target.dataset.wfTab, true);
      });
    }

    select(id, focus = false) {
      id = String(id);
      const tab = this.querySelector(`[data-wf-tab="${id}"]`);
      if (!tab) return;
      this.querySelectorAll('[data-wf-tab]').forEach((t) => {
        const active = t === tab;
        t.setAttribute('aria-selected', String(active));
        t.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[data-wf-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.wfPanel !== id;
      });
      if (focus) tab.focus();
    }
  }
  customElements.define('wf-tabs', WfTabs);
}

if (!customElements.get('wf-product-form')) {
  class WfProductForm extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.wf-main');
      this.data = JSON.parse(this.querySelector('[data-wf-data]').textContent);
      this.form = this.querySelector('[data-wf-form]');
      this.idInput = this.querySelector('[data-wf-id]');
      this.qtyInput = this.querySelector('[data-wf-qty-input]');
      this.mode = this.section.dataset.mode || 'onetime';
      this.variantId = Number(this.idInput.value);
      this.boxId = null;

      this.section.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.qtyInput.addEventListener('input', () => this.render());
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.bindGallery();
      this.prefetchGalleries();

      // Buttons elsewhere on the page (e.g. "Start my Subscription") can select subscribe and scroll here.
      // Document/window listeners are removed in disconnectedCallback because a size switch replaces this element.
      this.onSelectPlan = (event) => {
        const trigger = event.target.closest('[data-wf-select-plan]');
        if (!trigger) return;
        event.preventDefault();
        this.setMode(trigger.dataset.wfSelectPlan);
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      document.addEventListener('click', this.onSelectPlan);

      this.bindStickyCta();

      // Only one buy-box accordion open at a time. <details name> does this natively; this covers older browsers.
      this.querySelectorAll('.wf-buy__accordions details').forEach((item) => {
        item.addEventListener('toggle', () => {
          if (!item.open) return;
          this.querySelectorAll('.wf-buy__accordions details[open]').forEach((other) => {
            if (other !== item) other.open = false;
          });
        });
      });

      this.onResize = () => this.updateBoxArrows();
      this.boxTrack = this.querySelector('[data-wf-box-track]');
      if (this.boxTrack) {
        this.boxTrack.addEventListener('scroll', this.onResize, { passive: true });
        window.addEventListener('resize', this.onResize);
      }

      if (this.mode === 'subscribe') this.preselectFreeBox();
      this.render();
      this.updateBoxArrows();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onSelectPlan);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onCtaScroll);
      window.removeEventListener('resize', this.onCtaScroll);
    }

    // Sticky bar: shown once the in-page add to basket button has scrolled above the screen. Checked on
    // scroll (not IntersectionObserver) so jumps straight past it, e.g. the "scroll to reviews" link, still show it.
    bindStickyCta() {
      const dock = this.querySelector('[data-wf-cta-dock]');
      const bar = this.querySelector('[data-wf-sticky-bar]');
      if (!dock || !bar) return;
      const button = bar.querySelector('[data-wf-sticky-go]');
      let queued = false;
      const update = () => {
        queued = false;
        const show = dock.getBoundingClientRect().bottom < 0;
        if (show === bar.classList.contains('is-visible')) return;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        button.tabIndex = show ? 0 : -1;
      };
      this.onCtaScroll = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(update);
      };
      window.addEventListener('scroll', this.onCtaScroll, { passive: true });
      window.addEventListener('resize', this.onCtaScroll);
      update();

      // Back up to "Choose bag size"; the header slides back in while scrolling up, so leave room for it.
      button.addEventListener('click', () => {
        const target =
          this.querySelector('[data-wf-size-group]') || this.querySelector('[data-wf-plants-group]') || this.querySelector('.wf-buy');
        const header = document.querySelector('.section-header, sticky-header, header');
        const offset = (header ? header.offsetHeight : 0) + 16;
        window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
      });
    }

    updateBoxArrows() {
      if (!this.boxTrack) return;
      const max = this.boxTrack.scrollWidth - this.boxTrack.clientWidth - 2;
      this.querySelectorAll('[data-wf-box-scroll]').forEach((btn) => {
        const dir = Number(btn.dataset.wfBoxScroll);
        btn.disabled = dir < 0 ? this.boxTrack.scrollLeft <= 2 : this.boxTrack.scrollLeft >= max;
      });
    }

    get variant() {
      return this.data.variants.find((v) => v.id === this.variantId) || this.data.variants[0];
    }

    get qty() {
      return Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
    }

    get selectedBox() {
      if (!this.boxId) return null;
      return this.section.querySelector(`[data-wf-box="${this.boxId}"]`);
    }

    onClick(event) {
      if (event.target.closest('[data-wf-open^="wf-boxes-"]')) {
        this.drawerPick = null;
        this.render();
      }

      const modeBtn = event.target.closest('[data-wf-mode]');
      if (modeBtn) return this.setMode(modeBtn.dataset.wfMode);

      // "Subscribe instead" link in the one-time card.
      const switchBtn = event.target.closest('[data-wf-switch-mode]');
      if (switchBtn) return this.setMode(switchBtn.dataset.wfSwitchMode);

      const box = event.target.closest('[data-wf-box]');
      if (box) {
        const id = box.dataset.wfBox;
        this.boxId = this.boxId === id ? null : id;
        if (box.classList.contains('wf-box-card')) this.drawerPick = this.boxId;
        return this.render();
      }

      const qtyBtn = event.target.closest('[data-wf-qty]');
      if (qtyBtn) {
        this.qtyInput.value = Math.min(99, Math.max(1, this.qty + Number(qtyBtn.dataset.wfQty)));
        return this.render();
      }

      const scrollBtn = event.target.closest('[data-wf-box-scroll]');
      if (scrollBtn) {
        const track = this.querySelector('[data-wf-box-track]');
        track.scrollBy({ left: Number(scrollBtn.dataset.wfBoxScroll) * 150, behavior: 'smooth' });
      }
    }

    onChange(event) {
      if (!event.target.matches('[data-wf-variant]')) return;
      this.variantId = Number(event.target.value);
      this.querySelectorAll('.wf-tile--plant').forEach((tile) => {
        tile.classList.toggle('is-active', tile.contains(event.target));
      });
      // Written to the URL (?variant=) so a reload or shared link opens on this plant; a size switch keeps it.
      window.history.replaceState(window.history.state, '', wfUrlWithVariant(window.location.href, this.variantId));
      this.render();
      this.showVariantMedia();
      this.refreshGallery();
      // Re-prefetch the other sizes for this plant so a size switch lands on it without a wait.
      if (window.wfPrefetchSizes) window.wfPrefetchSizes();
      // Keep the "More info" plant styles drawer on the plant chosen in the buy box.
      this.section.querySelector('wf-tabs')?.select(this.variantId);
    }

    // Jump straight to the selected plant's photo (slides stay in the uploaded order).
    showVariantMedia() {
      const mediaId = this.variant.media;
      const slides = this.querySelector('[data-wf-slides]');
      const slide = mediaId && slides && slides.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide) return;
      slides.scrollTo({ left: Number(slide.dataset.index) * slides.clientWidth, behavior: 'instant' });
      this.syncThumbs(slides);
    }

    setMode(mode) {
      if (!this.querySelector(`[data-wf-mode="${mode}"]`)) return;
      this.mode = mode;
      // One-time: no window box until the shopper picks one. Subscribe: the free box is preselected.
      if (mode === 'onetime') this.boxId = null;
      if (mode === 'subscribe' && !this.boxId) this.preselectFreeBox();
      this.render();
    }

    preselectFreeBox() {
      if (this.dataset.preselectFree !== 'true') return;
      const free = this.querySelector('[data-wf-box][data-free="true"]:not([disabled])');
      if (free) this.boxId = free.dataset.wfBox;
    }

    format(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) return Shopify.formatMoney(cents, this.data.money);
      const value = (cents / 100).toFixed(2).replace(/\.00$/, '');
      return this.data.money.replace(/\{\{\s*\w+\s*\}\}/, value);
    }

    trim(money) {
      return money.replace(/[.,]00(?=\D*$)/, '');
    }

    render() {
      const v = this.variant;
      const isSub = this.mode === 'subscribe';
      const money = (c) => this.trim(this.format(c));
      const oneStrike = v.compare > v.price ? v.compare : 0;
      const subStrike = Math.max(v.price, v.compare);
      const subSaving = subStrike > v.subPrice ? Math.round(((subStrike - v.subPrice) * 100) / subStrike) : 0;

      this.section.dataset.mode = this.mode;
      this.querySelectorAll('[data-wf-mode]').forEach((btn) => {
        btn.setAttribute('aria-checked', String(btn.dataset.wfMode === this.mode));
      });

      this.setText('[data-wf-one-price]', money(v.price));
      this.setText('[data-wf-one-compare]', oneStrike ? money(oneStrike) : '');
      this.setText('[data-wf-sub-price]', money(v.subPrice));
      this.setText('[data-wf-sub-compare]', subSaving ? money(subStrike) : '');
      this.setText('[data-wf-save]', subSaving ? this.data.labels.save.replace('[percent]', subSaving) : '');

      this.section.querySelectorAll('[data-wf-box]').forEach((box) => {
        // Drawer cards only show a box picked in the drawer since it was opened (none preselected there).
        const inDrawer = box.classList.contains('wf-box-card');
        const active = box.dataset.wfBox === this.boxId && (!inDrawer || this.boxId === this.drawerPick);
        box.classList.toggle('is-active', active);
        box.setAttribute('aria-pressed', String(active));
      });

      const box = this.selectedBox;
      // One box per bag (see onSubmit), so a paid box is charged once per bag.
      const boxCharge = box && !(isSub && box.dataset.free === 'true') ? Number(box.dataset.price) * this.qty : 0;
      const unit = isSub ? v.subPrice : v.price;
      const unitStrike = isSub ? (subSaving ? subStrike : 0) : oneStrike;
      const total = unit * this.qty + boxCharge;
      // The struck-through total is what it would cost without the offers, so a free box counts at its price.
      const freeBoxValue = box && boxCharge === 0 ? (Number(box.dataset.price) || 0) * this.qty : 0;
      const totalStrike = unitStrike || freeBoxValue ? (unitStrike || unit) * this.qty + boxCharge + freeBoxValue : 0;

      const submit = this.querySelector('[data-wf-submit]');
      submit.disabled = !v.available;
      this.setText('[data-wf-cta-label]', v.available ? this.data.labels[this.mode] : this.data.labels.soldOut);
      this.setText('[data-wf-cta-price]', v.available ? money(total) : '');
      this.setText('[data-wf-cta-compare]', v.available && totalStrike ? money(totalStrike) : '');
      submit.classList.toggle('wf-cta__btn--no-price', !v.available);

      this.idInput.value = v.id;
    }

    setText(selector, text) {
      this.querySelectorAll(selector).forEach((el) => {
        el.textContent = text;
      });
    }

    bindGallery() {
      const gallery = this.querySelector('[data-wf-gallery]');
      gallery.addEventListener('click', (event) => {
        const slides = gallery.querySelector('[data-wf-slides]');
        const thumb = event.target.closest('[data-wf-thumb]');
        const arrow = event.target.closest('[data-wf-slide]');
        // Thumbnails jump instantly (no smooth-scroll wait); arrows keep the slide animation.
        if (thumb) this.goToSlide(slides, Number(thumb.dataset.wfThumb), 'instant');
        if (arrow) this.goToSlide(slides, this.currentSlide(slides) + Number(arrow.dataset.wfSlide));
      });
      this.warmGallery();
      gallery.addEventListener(
        'scroll',
        (event) => {
          if (!event.target.matches('[data-wf-slides]')) return;
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => this.syncThumbs(event.target), 60);
        },
        true
      );
    }

    currentSlide(slides) {
      return Math.round(slides.scrollLeft / slides.clientWidth);
    }

    goToSlide(slides, index, behavior = 'smooth') {
      const count = slides.children.length;
      const target = Math.min(Math.max(index, 0), count - 1);
      this.setActiveThumb(target);
      slides.scrollTo({ left: target * slides.clientWidth, behavior });
    }

    syncThumbs(slides) {
      this.setActiveThumb(this.currentSlide(slides));
    }

    setActiveThumb(index) {
      this.querySelectorAll('[data-wf-thumb]').forEach((thumb) => {
        thumb.classList.toggle('is-active', Number(thumb.dataset.wfThumb) === index);
      });
      // Mobile arrows fade out at either end (Figma), instead of wrapping round.
      const slides = this.querySelector('[data-wf-slides]');
      const last = slides ? slides.children.length - 1 : 0;
      this.querySelectorAll('[data-wf-slide]').forEach((arrow) => {
        arrow.disabled = Number(arrow.dataset.wfSlide) < 0 ? index <= 0 : index >= last;
      });
    }

    // Slides after the first are lazy; once the page is idle, load them so a thumbnail click shows the photo at once.
    warmGallery() {
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 800));
      idle(() => {
        this.querySelectorAll('.wf-gallery__slide img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      });
    }

    // Each plant can have its own gallery (variant metafield). Gallery markup per variant, fetched once with its
    // main photo and thumbnails decoded, so a plant click swaps instantly.
    galleryFor(variantId) {
      if (!this.galleries) this.galleries = new Map();
      if (!this.galleries.has(variantId)) {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        const view = new URL(window.location.href).searchParams.get('view');
        if (view) url.searchParams.set('view', view);
        url.searchParams.set('variant', variantId);
        const request = wfFetchSection(url, this.dataset.section).then((html) => {
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-wf-gallery]');
          if (!fresh) throw new Error('Gallery not rendered');
          const decodes = [...fresh.querySelectorAll('.wf-gallery__slide:first-child img, .wf-gallery__thumb img')].map(
            (source) => {
              const img = new Image();
              if (source.sizes) img.sizes = source.sizes;
              if (source.srcset) img.srcset = source.srcset;
              img.src = source.src;
              return img.decode().catch(() => {});
            }
          );
          // Don't hold the swap hostage to a slow image.
          return Promise.race([Promise.all(decodes), new Promise((resolve) => setTimeout(resolve, 1500))]).then(
            () => fresh.innerHTML
          );
        });
        request.catch(() => this.galleries.delete(variantId));
        this.galleries.set(variantId, request);
      }
      return this.galleries.get(variantId);
    }

    // Once the page is idle, fetch the other plants' galleries so switching plant needs no wait.
    prefetchGalleries() {
      const gallery = this.querySelector('[data-wf-gallery]');
      if (!this.galleries) this.galleries = new Map();
      if (gallery) this.galleries.set(this.variantId, Promise.resolve(gallery.innerHTML));
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
      idle(() => {
        this.querySelectorAll('[data-wf-variant]:not(:disabled)').forEach((input) => {
          this.galleryFor(Number(input.value)).catch(() => {});
        });
      });
    }

    refreshGallery() {
      const requestedId = this.variantId;
      this.galleryFor(requestedId)
        .then((html) => {
          const current = this.querySelector('[data-wf-gallery]');
          if (requestedId !== this.variantId || !current) return;
          current.innerHTML = html;
          this.warmGallery();
          // Without a plant gallery the slides are the product media; go to the plant's photo.
          this.showVariantMedia();
        })
        .catch(() => {});
    }

    onSubmit(event) {
      event.preventDefault();
      const submit = this.querySelector('[data-wf-submit]');
      if (submit.disabled || submit.getAttribute('aria-busy') === 'true') return;

      const error = this.querySelector('[data-wf-error]');
      error.hidden = true;
      submit.setAttribute('aria-busy', 'true');

      const isSub = this.mode === 'subscribe';
      const main = { id: this.variantId, quantity: this.qty };
      if (isSub && this.data.planId) main.selling_plan = this.data.planId;
      // Sizes are separate products with no Size option, so the cart drawer reads the chosen size from here.
      const sizeTile = this.querySelector('.wf-tile--size.is-active');
      if (sizeTile) main.properties = { _size: sizeTile.textContent.trim() };
      const items = [main];

      const box = this.selectedBox;
      if (box) {
        const properties = { _addon: String(this.variantId) };
        if (isSub && box.dataset.free === 'true') properties._free_with_subscription = 'true';
        // One box per bag, as the cart drawer keeps them: a box behind the bag quantity makes a Buy X get Y
        // discount split the bag into a second cart line.
        const boxItem = { id: Number(box.dataset.wfBox), quantity: this.qty, properties };
        // Boxes made free by a £0 selling plan go in with that plan, so the cart charges £0.
        if (isSub && box.dataset.freePlan) boxItem.selling_plan = Number(box.dataset.freePlan);
        items.push(boxItem);
      }

      const cart = document.querySelector('cart-drawer');
      const body = { items };
      if (cart && cart.getSectionsToRender) {
        body.sections = cart.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }

      const root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
      fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            error.textContent = response.description || response.message || this.data.labels.error;
            error.hidden = false;
            if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartError, { source: 'wf-product-form', errors: response.description });
            return;
          }
          if (!cart) {
            window.location = window.routes ? window.routes.cart_url : '/cart';
            return;
          }
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wf-product-form', productVariantId: this.variantId, cartData: response });
          }
          // The items are already in the cart. Shopify sometimes answers without the drawer HTML
          // (`sections: null`), so fetch it separately rather than failing.
          const ids = cart.getSectionsToRender().map((s) => s.id);
          const missing = !response.sections || ids.some((id) => !response.sections[id]);
          // A "Free box offer" block's code for this box wins over the size's code (empty = automatic discount).
          const code = box && box.dataset.freeOffer === 'true' ? box.dataset.freeCode : this.data.freeBoxCode;
          let ready;
          if (code && isSub && box && box.dataset.free === 'true') {
            // Apply this size's free-box code (keeping any codes already on the cart); the answer carries the drawer.
            ready = fetch(`${root}cart.js`)
              .then((res) => res.json())
              .then((current) => {
                const codes = (current.discount_codes || []).map((d) => d.code);
                if (!codes.some((c) => c.toLowerCase() === code.toLowerCase())) codes.push(code);
                return fetch(`${root}cart/update.js`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify({ discount: codes.join(','), sections: ids, sections_url: window.location.pathname }),
                });
              })
              .then((res) => res.json())
              .then((state) => (state.sections ? state : fetch(`${root}cart?sections=${ids.join(',')}`).then((res) => res.json()).then((sections) => ({ ...state, sections }))));
          } else {
            ready = missing
              ? fetch(`${root}cart?sections=${ids.join(',')}`)
                  .then((res) => res.json())
                  .then((sections) => ({ ...response, sections }))
              : Promise.resolve(response);
          }
          return ready.then((state) => {
            cart.classList.remove('is-empty');
            cart.renderContents(state);
          });
        })
        .catch((reason) => {
          // Logged so the console shows what actually failed (network, bad response, or the drawer render).
          console.error('[wf] add to cart failed', reason);
          error.textContent = this.data.labels.error;
          error.hidden = false;
        })
        .finally(() => {
          submit.removeAttribute('aria-busy');
        });
    }
  }
  customElements.define('wf-product-form', WfProductForm);
}

/*
  Size switch without a page reload. Small / Medium / Large are separate products, so we fetch the
  other product's buy box through the Section Rendering API and swap it in place. Subscribe/one-time
  and the chosen plant carry over (the URL stays plain, so a reload opens on the first plant). Any failure
  falls back to a normal page load.
*/
if (!window.wfSizeSwitch) {
  window.wfSizeSwitch = true;
  const cache = new Map();

  const sectionIdOf = (wrapper) => wrapper.id.replace('shopify-section-', '');

  const load = (href, sectionId) => {
    const key = `${href}#${sectionId}`;
    if (!cache.has(key)) cache.set(key, wfFetchSection(new URL(href, window.location.origin), sectionId));
    return cache.get(key).catch((error) => {
      cache.delete(key);
      throw error;
    });
  };

  // Download and decode the new main photo (and thumbnails) before swapping, so the gallery never flashes empty.
  const preloadImages = (fresh) => {
    const imgs = [...fresh.querySelectorAll('.wf-gallery__slide:first-child img, .wf-gallery__thumb img, .wf-tile--plant img')];
    const decodes = imgs.map((source) => {
      const img = new Image();
      if (source.sizes) img.sizes = source.sizes;
      if (source.srcset) img.srcset = source.srcset;
      img.src = source.src;
      return img.decode().catch(() => {});
    });
    // Don't hold the switch hostage to a slow image.
    return Promise.race([Promise.all(decodes), new Promise((resolve) => setTimeout(resolve, 1500))]);
  };

  const parseSection = (html, wrapper) => new DOMParser().parseFromString(html, 'text/html').getElementById(wrapper.id);

  // Name of the plant chosen in the buy box ("Moon Beam"), matched by name since each size has its own variants.
  const currentPlant = () =>
    document.querySelector('.wf-main .wf-tile--plant.is-active .wf-tile__name')?.textContent.trim() || null;

  // Variant id in `fresh` for the plant `plant`, or null when it is already the selected one.
  const plantVariantIn = (fresh, plant) => {
    if (!fresh || !plant) return null;
    const tile = [...fresh.querySelectorAll('.wf-tile--plant')].find(
      (el) => el.querySelector('.wf-tile__name')?.textContent.trim() === plant
    );
    const input = tile && tile.querySelector('[data-wf-variant]:not([disabled])');
    return input && !input.hasAttribute('checked') ? input.value : null;
  };

  // The other size's section, rendered with the same plant selected so it never flashes the first plant.
  const loadSize = (href, wrapper, plant) =>
    load(href, sectionIdOf(wrapper)).then((html) => {
      const fresh = parseSection(html, wrapper);
      const variantId = plantVariantIn(fresh, plant);
      if (!variantId) return fresh;
      const url = new URL(href, window.location.origin);
      url.searchParams.set('variant', variantId);
      return load(url.toString(), sectionIdOf(wrapper)).then((variantHtml) => parseSection(variantHtml, wrapper));
    });

  // Fetch the other sizes' sections for the current plant (and their photos) in the background once idle.
  const prefetchSizes = () => {
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
    idle(() => {
      const plant = currentPlant();
      document.querySelectorAll('.wf-main a.wf-tile--size[href]').forEach((link) => {
        const wrapper = link.closest('.shopify-section');
        if (!wrapper) return;
        loadSize(link.href, wrapper, plant)
          .then((fresh) => {
            if (fresh) preloadImages(fresh);
          })
          .catch(() => {});
      });
    });
  };
  window.wfPrefetchSizes = prefetchSizes;

  let switching = false;

  const swap = (href, wrapper, push, link) => {
    if (switching) return Promise.resolve();
    switching = true;

    const mode = wrapper.querySelector('.wf-main')?.dataset.mode;
    const plant = currentPlant();
    const oldTitle = wrapper.querySelector('.wf-buy__title')?.textContent.trim();

    // Show the new size as selected straight away instead of dimming the whole buy box.
    if (link) {
      wrapper.querySelectorAll('.wf-tile--size').forEach((tile) => tile.classList.toggle('is-active', tile === link));
    }
    wrapper.setAttribute('aria-busy', 'true');

    return loadSize(href, wrapper, plant)
      .then((fresh) => {
        const freshMain = fresh?.querySelector('.wf-main');
        if (!freshMain) throw new Error('Section not found');
        if (mode && freshMain.querySelector(`[data-wf-mode="${mode}"]`)) freshMain.dataset.mode = mode;
        return preloadImages(fresh).then(() => fresh);
      })
      .then((fresh) => {
        const scrollY = window.scrollY;
        wrapper.innerHTML = fresh.innerHTML;
        window.scrollTo({ top: scrollY, behavior: 'instant' });
        // The new size's URL with its selected plant, e.g. /products/bloom-bag-refill-small?variant=123.
        const url = wfUrlWithVariant(href, wrapper.querySelector('[data-wf-id]')?.value);
        if (push === 'replace') window.history.replaceState({ wfSize: true }, '', url);
        else if (push) window.history.pushState({ wfSize: true }, '', url);

        const newTitle = wrapper.querySelector('.wf-buy__title')?.textContent.trim();
        if (oldTitle && newTitle) document.title = document.title.replace(oldTitle, newTitle);
      })
      .catch((error) => {
        console.error('[wf] size switch failed, loading the page instead', error);
        window.location.href = href;
      })
      .finally(() => {
        switching = false;
        wrapper.removeAttribute('aria-busy');
      });
  };

  const sizeLink = (event) => {
    const link = event.target.closest && event.target.closest('.wf-main a.wf-tile--size[href]');
    if (!link || link.origin !== window.location.origin) return null;
    const wrapper = link.closest('.shopify-section');
    return wrapper ? { link, wrapper } : null;
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const hit = sizeLink(event);
    if (!hit) return;
    event.preventDefault();
    swap(hit.link.href, hit.wrapper, true, hit.link).then(prefetchSizes);
  });

  // Opened on a product that isn't one of the size products (an old or test link): the default size is
  // highlighted but the price, photos and cart would be that other product's. Load the real default size.
  const fixDefaultSize = () => {
    const link = document.querySelector('.wf-main a.wf-tile--size.is-active[href]');
    const wrapper = link && link.closest('.shopify-section');
    if (wrapper) swap(link.href, wrapper, 'replace', link).then(prefetchSizes);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fixDefaultSize, { once: true });
  else fixDefaultSize();

  if (document.readyState === 'complete') prefetchSizes();
  else window.addEventListener('load', prefetchSizes, { once: true });

  // Prefetch on hover / touch so the swap feels instant.
  ['pointerover', 'touchstart'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        const hit = sizeLink(event);
        if (hit) load(hit.link.href, sectionIdOf(hit.wrapper)).catch(() => {});
      },
      { passive: true }
    );
  });

  window.addEventListener('popstate', () => {
    const wrapper = document.querySelector('.wf-main')?.closest('.shopify-section');
    if (wrapper) swap(window.location.href, wrapper, false);
  });
}

// Rating stars with no link scroll so the reviews section starts at the top of the screen
// (the sticky header hides while scrolling down, so no offset is kept for it).
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-wf-to-reviews]');
  if (!link) return;
  const target = document.querySelector('.wf-reviews');
  if (!target) return;
  event.preventDefault();
  window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY, behavior: 'smooth' });
});
